import React, { useEffect, useState } from 'react';
import { Instagram, ArrowUp, Youtube, Music3 } from 'lucide-react';
import './Footer.css';
import { INLINE } from '../generated/inlineImages';
import { CONTACT_EMAIL } from '../constants/links';

const EXTERNAL = { target: '_blank', rel: 'noopener noreferrer' } as const;

const SECTION_LINKS = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Quiénes Somos', href: '#quienes-somos' },
  { label: 'Qué Hacemos', href: '#que-hacemos' },
  { label: 'Nuestros Valores', href: '#valores' },
  { label: 'Galería', href: '#galeria' },
  { label: 'Slogan', href: '#slogan' },
  { label: 'Cumbre CyC', href: '#cumbre-cyc' },
  { label: 'Ruta', href: '#ruta-nacional' },
  { label: 'Contacto', href: '#contacto' },
];

const TikTokIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_LINKS = [
  { icon: Instagram, href: 'https://www.instagram.com/unavozdiferente.pe/', label: 'Instagram UVD' },
  { icon: Instagram, href: 'https://www.instagram.com/cumbreciudadanoycreyente/', label: 'Instagram Cumbre' },
  { icon: Youtube, href: 'https://www.youtube.com/@UnaVozDiferente', label: 'YouTube' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@unavozdiferente.peru', label: 'TikTok' },
  { icon: Music3, href: 'https://open.spotify.com/show/3YDcxOvWyEeJ92W0gzpCBv?si=a56205ba97b34824&nd=1&dlsi=ab6b1ecd36d24db3', label: 'Spotify' },
];

function scrollToPageTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function FooterBrand() {
  return (
    <div className="footer__brand">
      <div className="footer__logo">
        <img src={INLINE.logoUvd} alt="Logo de Una Voz Diferente" />
        <div>
          <p className="footer__name">Una Voz Diferente</p>
          <p className="footer__tagline">Formando líderes para el Perú</p>
        </div>
      </div>
      <p className="footer__description">
        Una Voz Diferente (UVD) es una organización juvenil peruana sin fines de lucro. Promovemos el ejercicio responsable de la ciudadanía y el liderazgo con principios desde una cosmovisión cristiana.
      </p>
    </div>
  );
}

function FooterNav() {
  return (
    <nav className="footer__nav" aria-label="Secciones del sitio">
      <h2 className="footer__heading">Secciones</h2>
      <ul className="footer__link-list">
        {SECTION_LINKS.map((link) => (
          <li key={link.href}>
            <a href={link.href}>{link.label}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function FooterConnect() {
  return (
    <div className="footer__connect">
      <h2 className="footer__heading">Contacto</h2>
      <a className="footer__email" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
      <h2 className="footer__heading footer__heading--spaced">Redes</h2>
      <ul className="footer__social">
        {SOCIAL_LINKS.map((social) => (
          <li key={social.label}>
            <a href={social.href} {...EXTERNAL}>
              <social.icon size={16} aria-hidden="true" />
              {social.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useIsPastPageTop() {
  const [isPastTop, setIsPastTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsPastTop(window.scrollY > 240);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return isPastTop;
}

const Footer: React.FC = () => {
  const isPastTop = useIsPastPageTop();

  return (
    <footer className="footer">
      <div className="footer__identifier">
        <p className="footer__identifier-inner">
          Sitio oficial de Una Voz Diferente · Organización juvenil peruana
        </p>
      </div>
      <div className="footer__primary">
        <div className="footer__container">
          <FooterBrand />
          <FooterNav />
          <FooterConnect />
        </div>
      </div>
      <div className="footer__meta">
        <p className="footer__copyright">
          © 2026 Una Voz Diferente. Todos los derechos reservados.
        </p>
      </div>
      {isPastTop ? (
        <button
          type="button"
          className="footer__to-top"
          onClick={scrollToPageTop}
          aria-label="Volver arriba"
        >
          <ArrowUp size={18} aria-hidden="true" />
        </button>
      ) : null}
    </footer>
  );
};

export default Footer;
